import { PortalTradesTableProps } from '@/components/portal-modules/PortalTradesTable';
import { defaultColumn } from '@/config/config';
import { getTradeBookingColumns, TradeBookingRow } from '@/helpers/portalTradesHelper';
import { useAppSelector } from '@/state/hooks';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalTradesRow extends TradeBookingRow {}

interface UsePortalTradesTableProps extends PortalTradesTableProps {}

const usePortalTradesTable = (props: UsePortalTradesTableProps) => {
    const { data, isLoading } = props;

    const pagination = useAppSelector(selectTablePagination);

    const lang = useLanguage();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const columns: ColumnDef<TradeBookingRow>[] = useMemo(() => {
        if (data) return getTradeBookingColumns({ lang });
        else return [];
    }, [lang, data, isLoading]);

    const formattedPortalTrades: PortalTradesRow[] = useMemo(() => {
        if (data) return data;
        return [];
    }, [data]);

    const tableInstance = useReactTable({
        data: formattedPortalTrades,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: data ? Math.ceil(data.length / pagination.pageSize) : undefined,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalTrades, globalFilter };
};

export default usePortalTradesTable;
